import { z } from "zod";
import { AuditTrailEntryType } from "../AuditTrailEntryType.js";
// User / team / org / MFA / data-sharing audit `data` schemas. See `registry.ts` for
// the audit-data leniency contract these follow.
// Shared empty payload.
export const emptyUserDataSchema = z.object({});
// ===== Users =====
export const createUserDataSchema = z.object({
    email: z.string().nullish(), // staff PII
    name: z.string().nullish(), // staff PII
    isUsernamePasswordUser: z.boolean().nullish(),
    bulk: z.boolean().nullish(),
    count: z.number().nullish(),
});
export const updateUserEmailDataSchema = z.object({
    oldEmail: z.string().nullish(), // staff PII
    newEmail: z.string().nullish(), // staff PII
    isUsernamePasswordUser: z.boolean().nullish(),
});
export const convertGuestUserDataSchema = z.object({
    type: z.string().nullish(), // discriminates "user" | "team"
    name: z.string().nullish(), // staff PII (user branch)
    teamType: z.string().nullish(), // team branch
    parentOrganisationId: z.string().nullish(),
    teamName: z.string().nullish(), // team branch
});
// ===== Team / org membership =====
export const addUserToTeamDataSchema = z.object({
    role: z.string().nullish(),
    bulk: z.boolean().nullish(),
});
// Shared by addUserToManagingOrganisations / addUserToOrganisation.
export const roleGrantDataSchema = z.object({
    role: z.string().nullish(),
});
// Empty, or { bulk } on the bulk-configure path.
export const removeUserFromTeamDataSchema = z.object({
    bulk: z.boolean().nullish(),
});
// ===== Team / org lifecycle =====
// Shared by deleteTeam and deleteOrganisation; reason is "created-in-error" | "other".
export const deleteWithReasonDataSchema = z.object({
    reason: z.string().nullish(),
    reasonOther: z.string().nullish(), // free-text
    ignoreWorkItems: z.boolean().nullish(),
});
export const mergeTeamDataSchema = z.object({
    workItemsTransferred: z.number().nullish(),
    workItemsMerged: z.number().nullish(),
    membershipsAdded: z.number().nullish(),
});
export const updateOrganisationDataSchema = z.object({
    approverOne: z.string().nullish(),
    approverTwo: z.string().nullish(),
    ccmTicket: z.string().nullish(),
});
export const addOrganisationUserForManagingDataSchema = z.object({
    addedByUserId: z.string().nullish(),
    addedByUserName: z.string().nullish(), // staff PII
});
export const deleteOrganisationUserForManagingDataSchema = z.object({
    removedByUserId: z.string().nullish(),
    removedByUserName: z.string().nullish(), // staff PII
});
// ===== Email redirects =====
export const addEmailRedirectDataSchema = z.object({
    sourceEmail: z.string().nullish(), // staff PII
    targetTeamId: z.string().nullish(),
});
export const removeEmailRedirectDataSchema = z.object({
    id: z.string().nullish(),
});
// Audit type → schema entries for this file, composed in ../registry.ts.
export const userAuditDataRegistry = {
    // Users.
    [AuditTrailEntryType.createUser]: createUserDataSchema,
    [AuditTrailEntryType.updateUserEmail]: updateUserEmailDataSchema,
    [AuditTrailEntryType.deleteUser]: emptyUserDataSchema,
    [AuditTrailEntryType.convertGuestUser]: convertGuestUserDataSchema,
    [AuditTrailEntryType.convertFullUserToGuest]: emptyUserDataSchema,
    [AuditTrailEntryType.createIndependentContext]: emptyUserDataSchema,
    [AuditTrailEntryType.deleteSoadContext]: emptyUserDataSchema,
    [AuditTrailEntryType.deleteIndependentContext]: emptyUserDataSchema,
    [AuditTrailEntryType.updateUserProfile]: emptyUserDataSchema,
    [AuditTrailEntryType.passwordResetTriggered]: emptyUserDataSchema,
    [AuditTrailEntryType.resetAuthenticationMethods]: emptyUserDataSchema,
    [AuditTrailEntryType.revokeAllDeviceSessions]: emptyUserDataSchema,
    [AuditTrailEntryType.revokeDeviceSession]: emptyUserDataSchema,
    [AuditTrailEntryType.favouriteRecipientAdded]: emptyUserDataSchema,
    [AuditTrailEntryType.favouriteRecipientRemoved]: emptyUserDataSchema,
    [AuditTrailEntryType.acceptTermsAndConditions]: emptyUserDataSchema,
    // MFA.
    [AuditTrailEntryType.mfaEnforced]: emptyUserDataSchema,
    [AuditTrailEntryType.allowSmsMfa]: emptyUserDataSchema,
    [AuditTrailEntryType.disallowSmsMfa]: emptyUserDataSchema,
    [AuditTrailEntryType.mfaDisabled]: emptyUserDataSchema,
    [AuditTrailEntryType.resetMfa]: emptyUserDataSchema,
    // Logins.
    [AuditTrailEntryType.guestUserLoginSuccess]: emptyUserDataSchema,
    [AuditTrailEntryType.guestUserLoginFailure]: emptyUserDataSchema,
    [AuditTrailEntryType.userLoginSuccess]: emptyUserDataSchema,
    [AuditTrailEntryType.userLogout]: emptyUserDataSchema,
    // Teams.
    [AuditTrailEntryType.createTeam]: emptyUserDataSchema,
    [AuditTrailEntryType.deleteTeam]: deleteWithReasonDataSchema,
    [AuditTrailEntryType.updateTeam]: emptyUserDataSchema,
    [AuditTrailEntryType.mergeTeam]: mergeTeamDataSchema,
    [AuditTrailEntryType.addUserToTeam]: addUserToTeamDataSchema,
    [AuditTrailEntryType.addUserToManagingOrganisations]: roleGrantDataSchema,
    [AuditTrailEntryType.removeUserFromTeam]: removeUserFromTeamDataSchema,
    [AuditTrailEntryType.addUserToOrganisation]: roleGrantDataSchema,
    [AuditTrailEntryType.removeUserFromOrganisation]: emptyUserDataSchema,
    // Email redirects.
    [AuditTrailEntryType.addEmailRedirect]: addEmailRedirectDataSchema,
    [AuditTrailEntryType.removeEmailRedirect]: removeEmailRedirectDataSchema,
    // Organisations.
    [AuditTrailEntryType.createOrganisation]: emptyUserDataSchema,
    [AuditTrailEntryType.updateOrganisation]: updateOrganisationDataSchema,
    [AuditTrailEntryType.deleteOrganisation]: deleteWithReasonDataSchema,
    [AuditTrailEntryType.addOrganisationUserForManaging]: addOrganisationUserForManagingDataSchema,
    [AuditTrailEntryType.deleteOrganisationUserForManaging]: deleteOrganisationUserForManagingDataSchema,
    // Data-sharing partnerships (empty payloads).
    [AuditTrailEntryType.createDataSharingPartnership]: emptyUserDataSchema,
    [AuditTrailEntryType.deleteDataSharingPartnership]: emptyUserDataSchema,
    [AuditTrailEntryType.addDataControllerToDataSharingPartnership]: emptyUserDataSchema,
    [AuditTrailEntryType.removeDataControllerFromDataSharingPartnership]: emptyUserDataSchema,
};
//# sourceMappingURL=user.js.map